import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChartFrame } from '../../../components/chart/chart-frame';
import { FLOW_VAR } from '../../../components/chart/rag';
import { errorMessage } from '../../../lib/error-message';
import { reportApi, reportKeys } from '../api';

/**
 * Trạng thái tài liệu — spec mục 7, spec 16.4.
 *
 * Ba con số và một danh sách. Danh sách mới là phần có ích: "bao nhiêu bản
 * nháp" không ai làm gì được, còn "bản SRS này chờ duyệt 40 ngày rồi" thì có
 * người phải ra tay ngay.
 *
 * "Chờ bao lâu" đo từ lúc **vào** trạng thái chờ duyệt, không phải từ ngày tạo:
 * một tài liệu soạn ba tháng rồi mới gửi duyệt hôm qua thì không quá hạn gì cả.
 */
export function DocumentStatusChart({
  projectId,
  asOf,
}: {
  projectId: string;
  asOf?: string | undefined;
}): ReactNode {
  const { t } = useTranslation();

  const report = useQuery({
    queryKey: reportKeys.documentStatus(projectId, asOf ?? ''),
    queryFn: () => reportApi.documentStatus(projectId, asOf),
  });

  const data = report.data;
  const total = (data?.draft ?? 0) + (data?.review ?? 0) + (data?.approved ?? 0);

  const bars = [
    { label: t('reports.docDraft'), count: data?.draft ?? 0, color: FLOW_VAR.todo },
    { label: t('reports.docReview'), count: data?.review ?? 0, color: FLOW_VAR.inProgress },
    { label: t('reports.docApproved'), count: data?.approved ?? 0, color: FLOW_VAR.done },
  ];

  return (
    <ChartFrame
      title={t('reports.documentsTitle')}
      help="documents"
      subtitle={t('reports.documentsSubtitle', { count: total })}
      asOf={asOf ? new Date(asOf) : undefined}
      loading={report.isPending}
      error={report.isError ? errorMessage(report.error, t) : null}
      onRetry={() => void report.refetch()}
      empty={
        data && total === 0
          ? { title: t('reports.documentsEmpty'), description: t('reports.documentsEmptyHelp') }
          : null
      }
      legend={bars.map((bar) => ({ label: bar.label, color: bar.color }))}
    >
      <div className="flex flex-col gap-2">
        {bars.map((bar) => (
          <div key={bar.label} className="flex items-center gap-2 text-xs">
            <span className="w-20 shrink-0 text-muted-foreground">{bar.label}</span>
            <span className="h-3 flex-1 overflow-hidden rounded-full bg-muted">
              <span
                className="block h-full"
                style={{
                  width: `${total === 0 ? 0 : (bar.count / total) * 100}%`,
                  backgroundColor: bar.color,
                }}
              />
            </span>
            <span className="w-8 shrink-0 text-right font-medium">{bar.count}</span>
          </div>
        ))}

        {(data?.staleReview.length ?? 0) > 0 && (
          <div className="flex flex-col gap-0.5 pt-1">
            <p className="text-xs font-medium text-rag-amber">
              {t('reports.docStale', { days: data!.staleAfterDays })}
            </p>
            {data!.staleReview.slice(0, 5).map((document) => (
              <div key={document.documentId} className="flex items-baseline gap-2 text-xs">
                <span className="truncate">{document.title}</span>
                <span className="ml-auto shrink-0 text-rag-amber">
                  {t('reports.docWaiting', { days: document.daysWaiting })}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </ChartFrame>
  );
}
